import { User, getIdTokenResult, onAuthStateChanged, signOut } from 'firebase/auth';
import { auth, checkCurrentUserAdminStatus } from './firebase';

/**
 * Interface representing custom claims assigned to an administrator by Firebase Admin SDK.
 */
export interface AdminCustomClaims {
  admin?: boolean;
  role?: string;
  permissions?: string[];
  initializedAt?: number;
  isRootAdmin?: boolean;
  [key: string]: any;
}

/**
 * Result structure returned by admin custom claims verification.
 */
export interface AdminVerificationResult {
  isAdmin: boolean;
  mfaVerified?: boolean;
  mfaRequired?: boolean;
  claims: AdminCustomClaims | null;
  role: string | null;
  uid: string | null;
  email: string | null;
  error?: string;
}

/**
 * Primary helper function to verify Firebase Custom Claims.
 * Strictly verifies whether the authenticated user has the 'admin: true' custom claim
 * assigned by a trusted server/Cloud Function, ensuring that only authorized accounts
 * can access the protected /admin routes.
 *
 * @param user Optional Firebase User instance. Defaults to auth.currentUser.
 * @param forceRefresh Whether to force refresh the ID token against Firebase Auth servers.
 * @returns AdminVerificationResult
 */
export async function verifyAdminCustomClaims(
  user?: User | null,
  forceRefresh: boolean = true
): Promise<AdminVerificationResult> {
  const targetUser = user || auth.currentUser;

  if (!targetUser) {
    return {
      isAdmin: false,
      claims: null,
      role: null,
      uid: null,
      email: null,
      error: 'Unauthenticated: No user session found.',
    };
  }

  try {
    // Force refresh ensures newly provisioned claims (via initializeAdmin) are instantly recognized
    const tokenResult = await getIdTokenResult(targetUser, forceRefresh);
    const claims = (tokenResult.claims || {}) as AdminCustomClaims;

    // Authoritative check: User MUST have 'admin: true' claim AND an active /admins/{uid} record
    const hasAdminClaim = claims.admin === true;

    if (hasAdminClaim) {
      const serverStatus = await checkCurrentUserAdminStatus(targetUser);
      if (serverStatus.isAdmin && serverStatus.role) {
        return {
          isAdmin: true,
          mfaVerified: Boolean(serverStatus.mfaVerified),
          mfaRequired: false,
          claims,
          role: serverStatus.role,
          uid: targetUser.uid,
          email: serverStatus.email || targetUser.email || null,
        };
      }
      if (serverStatus.mfaRequired && serverStatus.role) {
        return {
          isAdmin: false,
          mfaVerified: false,
          mfaRequired: true,
          claims,
          role: serverStatus.role,
          uid: targetUser.uid,
          email: serverStatus.email || targetUser.email || null,
          error: 'Multi-factor authentication (2FA) is required for administrator access.',
        };
      }
      return {
        isAdmin: false,
        claims,
        role: null,
        uid: targetUser.uid,
        email: targetUser.email || null,
        error: 'Access Denied: Missing active server-managed /admins/{uid} administrator record.',
      };
    }

    return {
      isAdmin: false,
      claims,
      role: claims.role || null,
      uid: targetUser.uid,
      email: targetUser.email || null,
      error: 'Access Denied: Missing verified admin custom claims (claims.admin: true).',
    };
  } catch (err: any) {
    console.error('Failed to verify Firebase Custom Claims:', err);
    return {
      isAdmin: false,
      claims: null,
      role: null,
      uid: targetUser.uid,
      email: targetUser.email || null,
      error: err.message || 'Failed to verify custom claims due to an error.',
    };
  }
}

/**
 * Quick boolean check returning true only if the user has the 'admin: true' custom claim.
 */
export async function hasAdminClaim(
  user?: User | null,
  forceRefresh: boolean = false
): Promise<boolean> {
  const result = await verifyAdminCustomClaims(user, forceRefresh);
  return result.isAdmin;
}

/**
 * Retrieves the custom claims object of the target or currently authenticated user.
 */
export async function getAdminCustomClaims(
  user?: User | null,
  forceRefresh: boolean = false
): Promise<AdminCustomClaims | null> {
  const result = await verifyAdminCustomClaims(user, forceRefresh);
  return result.claims;
}

/**
 * Enforces admin authorization. Throws an Error if the user does not possess admin claims.
 */
export async function requireAdminAccess(
  user?: User | null
): Promise<AdminVerificationResult> {
  const result = await verifyAdminCustomClaims(user, true);
  if (!result.isAdmin) {
    throw new Error(
      result.error || 'Unauthorized: Verified admin custom claims (admin: true) required.'
    );
  }
  return result;
}

/**
 * Helper to get fresh ID token string for authenticated administrator to send to backend APIs.
 */
export async function getAdminIdToken(
  user?: User | null,
  forceRefresh: boolean = false
): Promise<string | null> {
  const targetUser = user || auth.currentUser;
  if (!targetUser) return null;
  return await targetUser.getIdToken(forceRefresh);
}

/**
 * Signs out the administrator from Firebase Auth and cleans up session tokens.
 */
export async function adminSignOut(): Promise<void> {
  try {
    await signOut(auth);
  } catch (err) {
    console.warn('Error signing out admin from Firebase:', err);
  }
}

/**
 * Subscribes to Firebase Auth state changes and validates admin claims reactively.
 * Returns an unsubscribe callback.
 */
export function listenToAdminAuthState(
  callback: (status: AdminVerificationResult) => void
): () => void {
  return onAuthStateChanged(auth, async (user) => {
    if (!user) {
      callback({
        isAdmin: false,
        claims: null,
        role: null,
        uid: null,
        email: null,
        error: 'Unauthenticated.',
      });
      return;
    }

    const verification = await verifyAdminCustomClaims(user, false);
    callback(verification);
  });
}

/**
 * Invokes the backend Cloud Function `setAdminClaim` to securely assign `admin: true`
 * and update the user's Firestore document.
 * Automatically attaches the authenticated requester's Firebase ID token for authorization.
 */
export async function callSetAdminClaim(params: {
  uid?: string;
  email?: string;
  role?: string;
}): Promise<{ success: boolean; message?: string; error?: string; claims?: AdminCustomClaims }> {
  try {
    const token = await getAdminIdToken(null, true);
    if (!token) {
      return { success: false, error: 'Caller must be authenticated to invoke setAdminClaim.' };
    }

    const response = await fetch('/api/functions/setAdminClaim', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(params),
    });

    const data = await response.json();
    return data;
  } catch (err: any) {
    return { success: false, error: err.message || 'Failed to call setAdminClaim function.' };
  }
}
